<template>
  <div class="item-web-options">
    <v-card outlined class="web-options-card">
      <v-card-title class="web-options-header">
        <span class="headline web-options-title">Web options</span>
        <v-spacer></v-spacer>
        <v-btn small color="primary" class="web-options-action" @click="resetOptionForm">
          New field
        </v-btn>
      </v-card-title>

      <v-card-text class="web-options-body">
        <v-alert type="info" dense text class="web-options-alert">
          These fields will appear on the webshop product page. For example: Color, Length, Custom length.
        </v-alert>

        <div class="locale-context-bar">
          <div class="locale-context-main">
            <v-chip small color="primary" text-color="white" class="locale-context-chip">
              {{ activeLocale.toUpperCase() }}
            </v-chip>
            <div>
              <div class="locale-context-title">
                Editing {{ activeLocale.toUpperCase() }} option texts
              </div>
              <div class="locale-context-description">
                Only options that have a {{ activeLocale.toUpperCase() }} translation are listed below. Keys, values, pricing and conditions remain shared.
              </div>
            </div>
          </div>
        </div>

        <v-form ref="webOptionForm" v-model="formValid" class="web-options-form">
          <div class="web-options-panel">
            <v-row class="form-row">
              <v-col cols="12" sm="3" md="3">
                <v-text-field
                  :value="getOptionTranslationField('label')"
                  :label="'Field label (' + activeLocale.toUpperCase() + ')'"
                  :rules="requiredRules"
                  :hint="'Required for the currently selected language.'"
                  persistent-hint
                  @input="setOptionTranslationField('label', $event)"
                  @blur="handleAutofillOptionKey"
                />
              </v-col>

              <v-col cols="12" sm="3" md="3">
                <v-text-field
                  v-model="optionForm.key"
                  label="Field key"
                  :rules="keyRules"
                  hint="Example: color, length, custom_length"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="2" md="2">
                <v-select
                  v-model="optionForm.type"
                  :items="fieldTypes"
                  label="Field type"
                  item-text="text"
                  item-value="value"
                  :rules="requiredRules"
                />
              </v-col>

              <v-col cols="12" sm="1" md="1">
                <v-text-field
                  v-model="optionForm.unit"
                  label="Unit"
                  hint="mm, m2, db"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="1" md="1">
                <v-text-field
                  v-model.number="optionForm.sort_order"
                  label="Order"
                  type="number"
                />
              </v-col>

              <v-col cols="12" sm="1" md="1" class="checkbox-col">
                <v-checkbox
                  v-model="optionForm.required"
                  label="Required"
                  color="primary"
                  hide-details
                />
              </v-col>

              <v-col cols="12" sm="2" md="2" class="checkbox-col">
                <v-checkbox
                  v-model="optionForm.is_extra"
                  label="Extra"
                  color="warning"
                  hide-details
                />
              </v-col>

              <v-col cols="12" sm="4" md="4" v-if="isChoiceField">
                <v-select
                  v-model="optionForm.default_value"
                  :items="sortedCurrentValues"
                  label="Default value"
                  :item-text="getLocalizedValueLabel"
                  item-value="value"
                  clearable
                  hint="Automatically selected value on the webshop product page"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="4" md="4" v-else>
                <v-text-field
                  v-model="optionForm.default_value"
                  label="Default value"
                  hint="Automatically filled value on the webshop product page"
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="2" md="2" class="checkbox-col">
                <v-checkbox
                  v-model="optionForm.is_locked"
                  label="Locked"
                  color="primary"
                  hide-details
                />
              </v-col>

              <v-col cols="12" sm="2" md="2" class="checkbox-col">
                <v-checkbox
                  v-model="optionForm.has_tooltip"
                  label="Tooltip"
                  color="primary"
                  hide-details
                />
              </v-col>

              <v-col cols="12" v-if="optionForm.has_tooltip">
                <v-textarea
                  :value="getOptionTranslationField('tooltip_text')"
                  :label="'Tooltip text (' + activeLocale.toUpperCase() + ')'"
                  rows="3"
                  auto-grow
                  hint="This text will be shown on the webshop when hovering the info icon."
                  persistent-hint
                  @input="setOptionTranslationField('tooltip_text', $event)"
                />
              </v-col>
            </v-row>

            <v-row v-if="optionForm.type === 'number'" class="form-row compact-row">
              <v-col cols="12" sm="3" md="3">
                <v-text-field
                  v-model.number="optionForm.validation_json.min"
                  label="Minimum"
                  type="number"
                />
              </v-col>
              <v-col cols="12" sm="3" md="3">
                <v-text-field
                  v-model.number="optionForm.validation_json.max"
                  label="Maximum"
                  type="number"
                />
              </v-col>
              <v-col cols="12" sm="3" md="3">
                <v-text-field
                  v-model.number="optionForm.validation_json.step"
                  label="Step"
                  type="number"
                />
              </v-col>
            </v-row>

            <div v-if="optionForm.type === 'repeater'" class="web-options-panel web-options-panel--muted repeater-config-panel">
              <div class="section-title-row">
                <h5 class="section-title">Repeater columns</h5>
                <v-btn small color="primary" text @click="handleAddRepeaterColumn">
                  Add column
                </v-btn>
              </div>

              <v-alert type="info" dense text class="web-options-alert">
                Use this for dynamic rows like cutting: 2 x 1.5 m and 1 x 2 m. The price is calculated from the rows.
              </v-alert>

              <v-row
                v-for="(column, columnIndex) in optionForm.metadata_json.repeater_columns"
                :key="'repeater-column-' + columnIndex"
                class="form-row compact-row repeater-column-row"
              >
                <v-col cols="12" sm="3" md="3">
                  <v-text-field
                    :value="getRepeaterColumnLabel(column)"
                    :label="'Column label (' + activeLocale.toUpperCase() + ')'"
                    @input="setRepeaterColumnLabel(column, $event)"
                    @blur="handleAutofillRepeaterColumnKey(columnIndex)"
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                    v-model="column.key"
                    label="Column key"
                    hint="quantity, length"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-select
                    v-model="column.type"
                    :items="repeaterColumnTypes"
                    label="Type"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2">
                  <v-text-field
                    v-model="column.unit"
                    label="Unit"
                    hint="m, db, mm"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="2" md="2" class="checkbox-col">
                  <v-checkbox
                    v-model="column.required"
                    label="Required"
                    color="primary"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="1" md="1" class="value-actions-col">
                  <v-btn icon small color="error" @click="handleRemoveRepeaterColumn(columnIndex)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-row class="form-row compact-row">
                <v-col cols="12" sm="3" md="3">
                  <v-select
                    v-model="optionForm.metadata_json.repeater_calculator.type"
                    :items="repeaterCalculatorTypes"
                    label="Calculator"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>
                <v-col cols="12" sm="3" md="3" v-if="optionForm.metadata_json.repeater_calculator.type === 'sum_product'">
                  <v-select
                    v-model="optionForm.metadata_json.repeater_calculator.quantity_field"
                    :items="repeaterColumnFieldItems"
                    label="Quantity field"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>
                <v-col cols="12" sm="3" md="3" v-if="optionForm.metadata_json.repeater_calculator.type === 'sum_product' || optionForm.metadata_json.repeater_calculator.type === 'sum_field'">
                  <v-select
                    v-model="optionForm.metadata_json.repeater_calculator.value_field"
                    :items="repeaterColumnFieldItems"
                    label="Value field"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>
              </v-row>
            </div>

            <div v-if="!isChoiceField" class="pricing-section">
              <h5 class="section-title">Pricing</h5>
              <v-alert type="info" dense text class="web-options-alert pricing-help-alert">
                Fixed amount is added once. Unit price × entered value multiplies the net amount by the number entered by the customer.
              </v-alert>
              <v-row class="form-row compact-row">
                <v-col cols="12" sm="4" md="4">
                  <v-select
                    v-model="optionForm.pricing_json.type"
                    :items="optionPricingTypes"
                    label="Pricing method"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>
                <v-col v-if="optionForm.pricing_json.type !== 'none'" cols="12" sm="4" md="4">
                  <v-text-field
                    v-model.number="optionForm.pricing_json.amount"
                    label="Net amount / unit price"
                    type="number"
                    suffix="HUF"
                    :hint="optionPricingHint"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </div>
          </div>

          <div class="web-options-panel web-options-panel--muted">
            <v-row class="form-row">
              <v-col cols="12" sm="3" md="3" class="checkbox-col">
                <v-checkbox
                  v-model="hasCondition"
                  label="Conditional display"
                  color="primary"
                  hide-details
                />
              </v-col>

              <v-col cols="12" sm="3" md="3" v-if="hasCondition">
                <v-select
                  v-model="conditionField"
                  :items="availableConditionFields"
                  label="Show if field"
                  item-text="label"
                  item-value="key"
                  clearable
                />
              </v-col>

              <v-col cols="12" sm="2" md="2" v-if="hasCondition">
                <v-select
                  v-model="conditionOperator"
                  :items="conditionOperators"
                  label="Operator"
                  item-text="text"
                  item-value="value"
                />
              </v-col>

              <v-col cols="12" sm="4" md="4" v-if="hasCondition">
                <v-text-field
                  v-model="conditionValue"
                  :label="conditionValueLabel"
                  :hint="conditionValueHint"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </div>

          <div class="web-options-panel web-options-panel--muted">
            <v-row class="form-row">
              <v-col cols="12" sm="3" md="2" class="checkbox-col">
                <v-checkbox
                  v-model="hasConditionalSurcharge"
                  label="Conditional surcharge"
                  color="warning"
                  hide-details
                />
              </v-col>

              <template v-if="hasConditionalSurcharge">
                <v-col cols="12" sm="3" md="2">
                  <v-select
                    v-model="conditionalSurchargeBasis"
                    :items="conditionalSurchargeBasisOptions"
                    label="Measured by"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>

                <v-col
                  v-if="conditionalSurchargeBasisNeedsOption"
                  cols="12"
                  sm="3"
                  md="2"
                >
                  <v-select
                    v-model="conditionalSurchargeBasisOptionKey"
                    :items="conditionalSurchargeBasisFieldItems"
                    label="Source field"
                    item-text="text"
                    item-value="value"
                    clearable
                  />
                </v-col>

                <v-col cols="12" sm="3" md="2">
                  <v-select
                    v-model="conditionalSurchargeOperator"
                    :items="conditionalSurchargeOperators"
                    label="Condition"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>

                <v-col cols="12" sm="3" md="2">
                  <v-text-field
                    v-model.number="conditionalSurchargeThreshold"
                    label="Limit"
                    type="number"
                    :suffix="conditionalSurchargeBasisUnit"
                    :rules="requiredRules"
                  />
                </v-col>

                <v-col cols="12" sm="3" md="2">
                  <v-select
                    v-model="conditionalSurchargeScope"
                    :items="conditionalSurchargeScopeOptions"
                    label="Scope"
                    item-text="text"
                    item-value="value"
                  />
                </v-col>
              </template>
            </v-row>

            <v-row v-if="hasConditionalSurcharge" class="form-row">
              <v-col
                v-if="conditionalSurchargeScope !== 'product'"
                cols="12"
                sm="4"
                md="3"
              >
                <v-text-field
                  v-model="conditionalSurchargeAggregationKey"
                  label="Surcharge key"
                  hint="Same key + same rule is charged only once in the order/group."
                  persistent-hint
                  :rules="keyRules"
                />
              </v-col>

              <v-col
                v-if="conditionalSurchargeScope === 'group'"
                cols="12"
                sm="4"
                md="3"
              >
                <v-select
                  v-model="conditionalSurchargeGroupOptionKey"
                  :items="conditionalSurchargeGroupFieldItems"
                  label="Group by field"
                  item-text="text"
                  item-value="value"
                  hint="Example: group all products with the same selected colour."
                  persistent-hint
                />
              </v-col>

              <v-col cols="12" sm="4" md="2">
                <v-select
                  v-model="conditionalSurchargeAmountType"
                  :items="conditionalSurchargeAmountTypeOptions"
                  label="Surcharge calculation"
                  item-text="text"
                  item-value="value"
                />
              </v-col>

              <v-col cols="12" sm="4" md="2">
                <v-text-field
                  v-model.number="conditionalSurchargeAmountNet"
                  label="Net surcharge"
                  type="number"
                  :suffix="conditionalSurchargeAmountSuffix"
                  :rules="requiredRules"
                />
              </v-col>

              <v-col cols="12" sm="8" md="4">
                <v-text-field
                  v-model="conditionalSurchargeLabel"
                  label="Label"
                  :hint="conditionalSurchargeDefaultLabel"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </div>

          <div v-if="isChoiceField" class="web-options-panel">
            <h5 class="section-title">Selectable values</h5>

            <v-row class="form-row">
              <v-col cols="12" sm="2" md="2">
                <v-text-field
                  :value="getValueTranslationField('label')"
                  :label="'Value label (' + activeLocale.toUpperCase() + ')'"
                  @input="setValueTranslationField('label', $event)"
                  @blur="handleAutofillValue"
                />
              </v-col>
              <v-col cols="12" sm="2" md="2">
                <v-text-field
                  v-model="valueForm.value"
                  label="Value key"
                  hint="Example: meggypiros, 700, custom"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" sm="2" md="2">
                <v-select
                  v-model="valueForm.pricing_json.type"
                  :items="valuePricingTypes"
                  label="Pricing method"
                  item-text="text"
                  item-value="value"
                />
              </v-col>
              <v-col v-if="valueForm.pricing_json.type !== 'none'" cols="12" sm="2" md="2">
                <v-text-field
                  v-model.number="valueForm.pricing_json.amount"
                  label="Net amount / unit price"
                  type="number"
                  suffix="HUF"
                  :hint="valuePricingHint"
                  persistent-hint
                />
              </v-col>
              <v-col cols="12" sm="2" md="2">
                <v-text-field
                  v-model.number="valueForm.sort_order"
                  label="Order"
                  type="number"
                />
              </v-col>
              <v-col cols="12" sm="2" md="2" class="value-actions-col">
                <v-btn small color="success" class="mr-2 value-action-button" @click="handleAddValue">
                  {{ editedValueIndex === -1 ? 'Add value' : 'Save value' }}
                </v-btn>
                <v-btn small text class="value-action-button" @click="resetValueForm" :disabled="editedValueIndex === -1">
                  Cancel
                </v-btn>
              </v-col>
            </v-row>

            <div class="table-card table-card--compact">
              <v-data-table
                dense
                :headers="valueHeaders"
                :items="sortedCurrentValues"
                :items-per-page="valueItemsPerPage"
                :footer-props="valueTableFooterProps"
                class="web-options-table elevation-0"
              >
                <template v-slot:item.label="{ item }">
                  <span v-if="getExactValueListLabel(item)" class="table-localized-label">
                    {{ getExactValueListLabel(item) }}
                  </span>
                  <span v-else class="missing-translation-label">
                    Missing {{ activeLocale.toUpperCase() }} translation
                  </span>
                </template>
                <template v-slot:item.pricing="{ item }">
                  {{ formatPricing(item.pricing_json) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon small class="mr-2 table-action-icon" @click="handleEditValue(item)">mdi-pencil</v-icon>
                  <v-icon small class="table-action-icon table-action-icon--danger" @click="handleDeleteValue(item)">mdi-delete</v-icon>
                </template>
              </v-data-table>
            </div>
          </div>

          <v-row class="form-footer-row">
            <v-col cols="12" class="form-footer">
              <v-btn color="success" class="mr-2 form-footer-button" @click="handleSaveOption">
                {{ editedOptionIndex === -1 ? 'Add field' : 'Save field' }}
              </v-btn>
              <v-btn class="form-footer-button" @click="cancelEditOption" :disabled="editedOptionIndex === -1">
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <div class="options-list-section">
          <div class="table-card">
            <v-data-table
              dense
              :headers="optionHeaders"
              :items="sortedOptions"
              :no-data-text="'No ' + activeLocale.toUpperCase() + ' options yet.'"
              :items-per-page="optionItemsPerPage"
              :footer-props="optionTableFooterProps"
              class="web-options-table elevation-0"
            >
              <template v-slot:item.label="{ item }">
                <span v-if="getExactOptionListLabel(item)" class="table-localized-label">
                  {{ getExactOptionListLabel(item) }}
                </span>
                <span v-else class="missing-translation-label">
                  Missing {{ activeLocale.toUpperCase() }} translation
                </span>
              </template>

              <template v-slot:item.required="{ item }">
                <v-chip x-small :color="item.required ? 'primary' : ''" class="required-chip">
                  {{ item.required ? 'Yes' : 'No' }}
                </v-chip>
              </template>

              <template v-slot:item.is_extra="{ item }">
                <v-chip x-small :color="item.is_extra ? 'warning' : ''" class="required-chip">
                  {{ item.is_extra ? 'Yes' : 'No' }}
                </v-chip>
              </template>

              <template v-slot:item.default_value="{ item }">
                <span v-if="item.default_value !== null && item.default_value !== undefined && item.default_value !== ''" class="table-muted-text">
                  {{ item.default_value }}
                </span>
                <span v-else class="table-empty-text">-</span>
              </template>

              <template v-slot:item.is_locked="{ item }">
                <v-chip x-small :color="item.is_locked ? 'primary' : ''" class="required-chip">
                  {{ item.is_locked ? 'Yes' : 'No' }}
                </v-chip>
              </template>

              <template v-slot:item.tooltip="{ item }">
                <span v-if="item.has_tooltip && getExactOptionTooltip(item)" class="table-muted-text">
                  {{ getExactOptionTooltip(item) }}
                </span>
                <span v-else class="table-empty-text">-</span>
              </template>

              <template v-slot:item.values="{ item }">
                <span v-if="(isChoiceOption(item) || item.type === 'repeater') && formatCurrentLocaleValues(item.values)" class="table-muted-text">
                  {{ formatCurrentLocaleValues(item.values) }}
                </span>
                <span v-else class="table-empty-text">-</span>
              </template>

              <template v-slot:item.pricing="{ item }">
                <span v-if="isChoiceOption(item)" class="table-muted-text">
                  Per selectable value
                </span>
                <span v-else class="table-muted-text">
                  {{ formatPricing(item.pricing_json) }}
                </span>
              </template>

              <template v-slot:item.validation="{ item }">
                <span v-if="item.validation_json && Object.keys(item.validation_json).length" class="table-muted-text">
                  min: {{ item.validation_json.min || '-' }},
                  max: {{ item.validation_json.max || '-' }},
                  step: {{ item.validation_json.step || '-' }}
                </span>
                <span v-else class="table-empty-text">-</span>
              </template>

              <template v-slot:item.condition="{ item }">
                <span v-if="item.conditions_json && item.conditions_json.show_if" class="table-muted-text">
                  {{ formatCondition(item.conditions_json.show_if) }}
                </span>
                <span v-else class="table-empty-text">-</span>
              </template>

              <template v-slot:item.conditional_surcharge="{ item }">
                <span v-if="item.conditional_surcharge_json && item.conditional_surcharge_json.enabled" class="table-muted-text">
                  {{ formatConditionalSurcharge(item.conditional_surcharge_json) }}
                </span>
                <span v-else class="table-empty-text">-</span>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-icon small class="mr-2 table-action-icon" @click="handleEditOption(item)">mdi-pencil</v-icon>
                <v-icon small class="table-action-icon table-action-icon--danger" @click="handleDeleteOption(item)">mdi-delete</v-icon>
              </template>
            </v-data-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

const initialPricing = () => ({
  type: 'none',
  amount: 0,
});

const initialOptionForm = () => ({
  id: null,
  item_id: null,
  key: '',
  label: '',
  translations: {},
  type: 'select',
  unit: '',
  required: false,
  is_extra: false,
  default_value: null,
  is_locked: false,
  has_tooltip: false,
  tooltip_text: null,
  sort_order: 0,
  validation_json: {},
  conditions_json: null,
  conditional_surcharge_json: null,
  pricing_json: initialPricing(),
  price_modifier: 0,
  metadata_json: {
    price_modifier_type: 'fixed',
  },
  values: [],
});

const initialValueForm = () => ({
  id: null,
  item_option_id: null,
  value: '',
  label: '',
  translations: {},
  pricing_json: initialPricing(),
  price_modifier: 0,
  sort_order: 0,
  metadata_json: {
    price_modifier_type: 'fixed',
  },
});

export default {
  name: 'ItemWebOptions',
  props: {
    value: {
      type: Array,
      default: function () {
        return [];
      },
    },
    languages: {
      type: Array,
      default: function () {
        return [];
      },
    },
    selectedLocale: {
      type: [Object, String],
      default: function () {
        return { lang: 'hu' };
      },
    },
  },
  data() {
    return {
      localOptions: [],
      formValid: false,
      optionForm: initialOptionForm(),
      valueForm: initialValueForm(),
      editedOptionIndex: -1,
      editedValueIndex: -1,
      valueItemsPerPage: 10,
      optionItemsPerPage: 10,
      valueTableFooterProps: {
        itemsPerPageOptions: [10, 20, 50, -1],
        itemsPerPageText: 'Values per page',
        showFirstLastPage: true,
      },
      optionTableFooterProps: {
        itemsPerPageOptions: [10, 20, 50, -1],
        itemsPerPageText: 'Fields per page',
        showFirstLastPage: true,
      },
      hasCondition: false,
      conditionField: null,
      conditionOperator: '=',
      conditionValue: null,
      hasConditionalSurcharge: false,
      conditionalSurchargeBasis: 'square_meter',
      conditionalSurchargeBasisOptionKey: null,
      conditionalSurchargeOperator: 'below',
      conditionalSurchargeThreshold: null,
      conditionalSurchargeAmountType: 'fixed',
      conditionalSurchargeAmountNet: null,
      conditionalSurchargeScope: 'product',
      conditionalSurchargeAggregationKey: '',
      conditionalSurchargeGroupOptionKey: null,
      conditionalSurchargeLabel: '',
      conditionOperators: [
        { text: 'Equals (=)', value: '=' },
        { text: 'Not equals (!=)', value: '!=' },
        { text: 'Greater than (>)', value: '>' },
        { text: 'Greater or equal (>=)', value: '>=' },
        { text: 'Less than (<)', value: '<' },
        { text: 'Less or equal (<=)', value: '<=' },
        { text: 'Is one of (in)', value: 'in' },
        { text: 'Is not one of (not in)', value: 'not_in' },
      ],
      conditionalSurchargeBasisOptions: [
        { text: 'Calculated square meter', value: 'square_meter' },
        { text: 'Product quantity', value: 'quantity' },
        { text: 'Numeric field value', value: 'option_value' },
        { text: 'Repeater calculated total', value: 'repeater_total' },
      ],
      conditionalSurchargeAmountTypeOptions: [
        { text: 'Fixed amount once', value: 'fixed' },
        { text: 'Amount × measured value', value: 'per_basis_unit' },
      ],
      conditionalSurchargeScopeOptions: [
        { text: 'Per product line', value: 'product' },
        { text: 'Once per order', value: 'order' },
        { text: 'Per matching group', value: 'group' },
      ],
      conditionalSurchargeOperators: [
        { text: 'Below (<)', value: 'below' },
        { text: 'Below or equal (<=)', value: 'below_or_equal' },
        { text: 'Above (>)', value: 'above' },
        { text: 'Above or equal (>=)', value: 'above_or_equal' },
      ],
      fieldTypes: [
        { text: 'Dropdown', value: 'select' },
        { text: 'Radio', value: 'radio' },
        { text: 'Checkbox', value: 'checkbox' },
        { text: 'Number', value: 'number' },
        { text: 'Text', value: 'text' },
        { text: 'Textarea', value: 'textarea' },
        { text: 'Repeater / dynamic rows', value: 'repeater' },
      ],
      pricingTypes: [
        { text: 'No price effect', value: 'none' },
        { text: 'One-time fixed net amount', value: 'fixed' },
        { text: 'Net unit price × entered value', value: 'multiply_input' },
        { text: 'Net unit price × calculated m²', value: 'square_meter' },
        { text: 'Net unit price × repeater total', value: 'repeater_total' },
      ],
      optionHeaders: [
        { text: 'Order', value: 'sort_order' },
        { text: 'Label', value: 'label' },
        { text: 'Key', value: 'key' },
        { text: 'Type', value: 'type' },
        { text: 'Unit', value: 'unit' },
        { text: 'Required', value: 'required' },
        { text: 'Extra', value: 'is_extra' },
        { text: 'Default', value: 'default_value' },
        { text: 'Locked', value: 'is_locked' },
        { text: 'Pricing', value: 'pricing', sortable: false },
        //{ text: 'Tooltip', value: 'tooltip', sortable: false },
        { text: 'Values', value: 'values', sortable: false },
        { text: 'Validation', value: 'validation', sortable: false },
        { text: 'Condition', value: 'condition', sortable: false },
        { text: 'Conditional surcharge', value: 'conditional_surcharge', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      repeaterColumnTypes: [
        { text: 'Number', value: 'number' },
        { text: 'Text', value: 'text' },
      ],
      repeaterCalculatorTypes: [
        { text: 'Sum product: quantity x value', value: 'sum_product' },
        { text: 'Sum one field', value: 'sum_field' },
        { text: 'Row count', value: 'row_count' },
      ],
      valueHeaders: [
        { text: 'Order', value: 'sort_order' },
        { text: 'Label', value: 'label' },
        { text: 'Value', value: 'value' },
        { text: 'Pricing', value: 'pricing', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      requiredRules: [(v) => !!v || 'Field is required'],
      keyRules: [
        (v) => !!v || 'Key is required',
        (v) => /^[a-z0-9_]+$/.test(v || '') || 'Only lowercase letters, numbers and underscore allowed',
      ],
    };
  },
  computed: {
    activeLocale() {
      const locale = typeof this.selectedLocale === 'string'
        ? this.selectedLocale
        : (this.selectedLocale && this.selectedLocale.lang);

      return this.normalizeLocale(locale || 'hu');
    },
    availableLocaleCodes() {
      const localeCodes = (this.languages || [])
        .map((language) => {
          if (typeof language === 'string') {
            return this.normalizeLocale(language);
          }

          return this.normalizeLocale(
            language.lang
            || language.locale
            || language.code
            || language.value
            || ''
          );
        })
        .filter((locale) => locale);

      if (!localeCodes.length) {
        return ['hu', 'en', 'ro'];
      }

      return Array.from(new Set(localeCodes));
    },
    isChoiceField() {
      return ['select', 'dropdown', 'radio', 'checkbox'].includes(this.optionForm.type);
    },
    sortedOptions() {
      return this.clone(this.localOptions)
        .filter((option) => this.hasOptionTranslation(option, this.activeLocale))
        .sort((a, b) => {
          return Number(a.sort_order || 0) - Number(b.sort_order || 0);
        });
    },
    sortedCurrentValues() {
      return this.clone(this.optionForm.values || []).sort((a, b) => {
        return Number(a.sort_order || 0) - Number(b.sort_order || 0);
      });
    },
    repeaterColumnFieldItems() {
      const metadata = this.optionForm.metadata_json || {};
      const columns = Array.isArray(metadata.repeater_columns) ? metadata.repeater_columns : [];

      return columns
        .filter((column) => column && column.key)
        .map((column) => ({
          text: (this.getRepeaterColumnLabel(column) || column.key) + ' (' + column.key + ')',
          value: column.key,
        }));
    },
    availableConditionFields() {
      return this.localOptions
        .filter((option) => {
          return option.key !== this.optionForm.key
            && this.hasOptionTranslation(option, this.activeLocale);
        })
        .map((option) => ({
          key: option.key,
          label: this.getExactOptionListLabel(option) + ' (' + option.key + ')',
        }));
    },
    conditionValueLabel() {
      return this.isMultiConditionOperator(this.conditionOperator) ? 'Values' : 'Value';
    },
    conditionValueHint() {
      if (this.isMultiConditionOperator(this.conditionOperator)) {
        return 'Separate multiple values with comma, semicolon or new line. Example: fekete, kek, feher';
      }

      if (this.isComparisonConditionOperator(this.conditionOperator)) {
        return 'Example: 100, 0.45 or 0_45';
      }

      return 'Example: custom or 0_45';
    },
    conditionalSurchargeBasisNeedsOption() {
      return ['square_meter', 'option_value', 'repeater_total'].includes(
        this.conditionalSurchargeBasis
      );
    },
    conditionalSurchargeBasisFieldItems() {
      return this.getConditionalSurchargeFieldItems(this.conditionalSurchargeBasis);
    },
    conditionalSurchargeGroupFieldItems() {
      return this.getAllOptionFieldItems();
    },
    conditionalSurchargeBasisUnit() {
      return this.getConditionalSurchargeBasisUnit(this.conditionalSurchargeBasis);
    },
    conditionalSurchargeAmountSuffix() {
      if (this.conditionalSurchargeAmountType === 'per_basis_unit') {
        return 'HUF/' + this.conditionalSurchargeBasisUnit;
      }

      return 'HUF';
    },
    conditionalSurchargeDefaultLabel() {
      const limit = this.conditionalSurchargeThreshold || '…';
      return 'Example: ' + limit + ' ' + this.conditionalSurchargeBasisUnit + ' alatti felár';
    },
    optionPricingTypes() {
      return this.getPricingTypesForField(this.optionForm.type);
    },
    valuePricingTypes() {
      return this.pricingTypes.filter((item) => {
        return ['none', 'fixed', 'multiply_input', 'square_meter'].includes(item.value);
      });
    },
    optionPricingHint() {
      return this.pricingHintByType(this.optionForm.pricing_json.type);
    },
    valuePricingHint() {
      return this.pricingHintByType(this.valueForm.pricing_json.type);
    },
  },
  watch: {
    activeLocale() {
      this.resetOptionForm();
    },
    value: {
      handler(value) {
        this.localOptions = this.clone(value || []).map((option) => this.normalizeOption(option));
      },
      immediate: true,
    },
    'optionForm.type'(type) {
      if (type === 'repeater') {
        this.ensureRepeaterMetadata();
      }

      if (!this.optionForm.pricing_json) {
        this.$set(this.optionForm, 'pricing_json', initialPricing());
      }

      const allowedTypes = this.getPricingTypesForField(type).map((item) => item.value);

      if (!allowedTypes.includes(this.optionForm.pricing_json.type)) {
        this.optionForm.pricing_json.type = type === 'repeater'
          ? 'repeater_total'
          : 'none';
      }

      if (this.optionForm.pricing_json.type === 'none') {
        this.optionForm.pricing_json.amount = 0;
      }

      if (['select', 'dropdown', 'radio', 'checkbox'].includes(type)) {
        return;
      }

      // Non-choice fields keep one internal value for legacy webshop compatibility.
      if (!Array.isArray(this.optionForm.values)) {
        this.optionForm.values = [];
      }
    },
    conditionalSurchargeBasis(basis) {
      if (!['square_meter', 'option_value', 'repeater_total'].includes(basis)) {
        this.conditionalSurchargeBasisOptionKey = null;
        return;
      }

      const availableKeys = this.getConditionalSurchargeFieldItems(basis)
        .map((item) => item.value);

      if (!availableKeys.includes(this.conditionalSurchargeBasisOptionKey)) {
        this.conditionalSurchargeBasisOptionKey = availableKeys.includes(this.optionForm.key)
          ? this.optionForm.key
          : (availableKeys[0] || null);
      }
    },
    conditionalSurchargeScope(scope) {
      if (scope === 'product') {
        this.conditionalSurchargeAggregationKey = '';
        this.conditionalSurchargeGroupOptionKey = null;
        return;
      }

      if (!this.conditionalSurchargeAggregationKey && this.optionForm.key) {
        this.conditionalSurchargeAggregationKey = this.optionForm.key + '_conditional_surcharge';
      }

      if (scope === 'group' && !this.conditionalSurchargeGroupOptionKey) {
        this.conditionalSurchargeGroupOptionKey = this.optionForm.key || null;
      }

      if (scope !== 'group') {
        this.conditionalSurchargeGroupOptionKey = null;
      }
    },
    'optionForm.pricing_json.type'(type) {
      if (type === 'none') {
        this.optionForm.pricing_json.amount = 0;
      }
    },
    'valueForm.pricing_json.type'(type) {
      if (type === 'none') {
        this.valueForm.pricing_json.amount = 0;
      }
    },
  },
  methods: {
    showError(message) {
      return Swal.fire({
        title: 'Error',
        text: message,
        icon: 'error',
        confirmButtonText: 'OK',
      });
    },
    async confirmDelete(title, text) {
      const result = await Swal.fire({
        title: title,
        text: text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#d33',
      });

      return !!(result.isConfirmed || result.value);
    },
    clone(data) {
      return JSON.parse(JSON.stringify(data || []));
    },
    normalizeLocale(locale) {
      return String(locale || 'hu').trim().toLowerCase().replace('_', '-');
    },
    normalizeTranslations(translations, legacyData, allowedFields) {
      const normalized = {};
      const source = translations && typeof translations === 'object' && !Array.isArray(translations)
        ? translations
        : {};

      Object.keys(source).forEach((locale) => {
        const normalizedLocale = this.normalizeLocale(locale);
        const translation = source[locale] && typeof source[locale] === 'object'
          ? source[locale]
          : {};

        normalized[normalizedLocale] = {};
        allowedFields.forEach((field) => {
          normalized[normalizedLocale][field] = translation[field] === null || translation[field] === undefined
            ? null
            : String(translation[field]);
        });
      });

      // A legacy label csak akkor jelent magyar fordítást, ha még egyetlen
      // külön fordítás sincs. Így egy EN/RO-only optionből nem készül hamis HU fordítás.
      if (!Object.keys(normalized).length && legacyData) {
        const legacyTranslation = {};
        let hasLegacyValue = false;

        allowedFields.forEach((field) => {
          const legacyValue = legacyData[field];
          legacyTranslation[field] = legacyValue === null || legacyValue === undefined
            ? null
            : String(legacyValue);

          if (legacyTranslation[field] !== null && legacyTranslation[field].trim() !== '') {
            hasLegacyValue = true;
          }
        });

        if (hasLegacyValue) {
          normalized.hu = legacyTranslation;
        }
      }

      return normalized;
    },
    ensureTranslation(target, locale, allowedFields) {
      const normalizedLocale = this.normalizeLocale(locale || this.activeLocale);

      if (!target.translations || typeof target.translations !== 'object' || Array.isArray(target.translations)) {
        this.$set(target, 'translations', {});
      }

      if (!target.translations[normalizedLocale]) {
        const emptyTranslation = {};
        allowedFields.forEach((field) => {
          emptyTranslation[field] = null;
        });
        this.$set(target.translations, normalizedLocale, emptyTranslation);
      }

      return target.translations[normalizedLocale];
    },
    getExactTranslationField(target, field, locale) {
      const normalizedLocale = this.normalizeLocale(locale || this.activeLocale);
      const translations = target && target.translations && typeof target.translations === 'object'
        ? target.translations
        : {};
      const translation = translations[normalizedLocale];

      if (!translation || translation[field] === null || translation[field] === undefined) {
        return '';
      }

      return translation[field];
    },
    setTranslationField(target, field, value, allowedFields) {
      const translation = this.ensureTranslation(target, this.activeLocale, allowedFields);
      this.$set(translation, field, value === null || value === undefined ? null : String(value));

      if (this.activeLocale === 'hu') {
        this.$set(target, field, value === null || value === undefined ? null : String(value));
      }
    },
    getLocalizedTranslationField(target, field, fallbackField) {
      if (!target) {
        return '';
      }

      const translations = target.translations && typeof target.translations === 'object'
        ? target.translations
        : {};
      const candidates = [this.activeLocale, this.activeLocale.split('-')[0], 'hu'];

      for (let index = 0; index < candidates.length; index += 1) {
        const candidate = candidates[index];
        const translation = translations[candidate];
        const value = translation ? translation[field] : null;

        if (value !== null && value !== undefined && String(value).trim() !== '') {
          return value;
        }
      }

      return target[fallbackField || field] || target.key || target.value || '';
    },
    getOptionTranslationField(field) {
      return this.getExactTranslationField(this.optionForm, field, this.activeLocale);
    },
    setOptionTranslationField(field, value) {
      this.setTranslationField(this.optionForm, field, value, ['label', 'tooltip_text']);
    },
    getValueTranslationField(field) {
      return this.getExactTranslationField(this.valueForm, field, this.activeLocale);
    },
    setValueTranslationField(field, value) {
      this.setTranslationField(this.valueForm, field, value, ['label']);
    },
    getLocalizedOptionLabel(option) {
      return this.getLocalizedTranslationField(option, 'label', 'label');
    },
    getLocalizedValueLabel(value) {
      return this.getLocalizedTranslationField(value, 'label', 'label');
    },
    getExactOptionListLabel(option) {
      return String(this.getExactTranslationField(option, 'label', this.activeLocale) || '').trim();
    },
    getExactOptionTooltip(option) {
      return String(this.getExactTranslationField(option, 'tooltip_text', this.activeLocale) || '').trim();
    },
    getExactValueListLabel(value) {
      return String(this.getExactTranslationField(value, 'label', this.activeLocale) || '').trim();
    },
    hasOptionTranslation(option, locale) {
      return !!String(this.getExactTranslationField(option, 'label', locale) || '').trim();
    },
    getTranslationChipColor(option, locale) {
      if (!this.hasOptionTranslation(option, locale)) {
        return locale === this.activeLocale ? 'error' : '';
      }

      return locale === this.activeLocale ? 'primary' : 'success';
    },
    getTranslationChipTextColor(option, locale) {
      return this.hasOptionTranslation(option, locale) ? 'white' : undefined;
    },
    formatCurrentLocaleValues(values) {
      return (values || [])
        .map((value) => this.getExactValueListLabel(value))
        .filter((label) => !!label)
        .join(', ');
    },
    getRepeaterColumnLabel(column) {
      return this.getLocalizedTranslationField(column, 'label', 'label');
    },
    setRepeaterColumnLabel(column, value) {
      this.setTranslationField(column, 'label', value, ['label']);
    },
    normalizePricingType(type) {
      const normalizedType = String(type || 'fixed').trim().toLowerCase();
      const aliases = {
        no_price: 'none',
        disabled: 'none',
        per_input: 'multiply_input',
        per_meter: 'multiply_input',
        input: 'multiply_input',
        sqm: 'square_meter',
        m2: 'square_meter',
        per_repeater_total: 'repeater_total',
        per_dynamic_total: 'repeater_total',
      };
      const resolvedType = aliases[normalizedType] || normalizedType;
      const supportedTypes = ['none', 'fixed', 'multiply_input', 'square_meter', 'repeater_total'];

      return supportedTypes.includes(resolvedType) ? resolvedType : 'fixed';
    },
    normalizePricing(pricing, legacyAmount, legacyType) {
      if (typeof pricing === 'string') {
        try {
          pricing = JSON.parse(pricing);
        } catch (e) {
          pricing = null;
        }
      }

      const source = pricing && typeof pricing === 'object' && !Array.isArray(pricing)
        ? pricing
        : {};
      const type = this.normalizePricingType(
        source.type !== undefined ? source.type : (legacyType || 'fixed')
      );
      const rawAmount = source.amount !== undefined ? source.amount : legacyAmount;
      const amount = Number(rawAmount || 0);

      if (type === 'none' || !Number.isFinite(amount) || Math.abs(amount) < 0.00001) {
        return initialPricing();
      }

      return {
        type: type,
        amount: Math.round(amount * 100) / 100,
      };
    },
    pricingToLegacyType(type) {
      const normalizedType = this.normalizePricingType(type);

      if (normalizedType === 'multiply_input') {
        return 'per_input';
      }

      if (normalizedType === 'repeater_total') {
        return 'per_repeater_total';
      }

      if (normalizedType === 'square_meter') {
        return 'square_meter';
      }

      return 'fixed';
    },
    getPricingTypesForField(fieldType) {
      let allowedTypes = ['none', 'fixed'];

      if (fieldType === 'number') {
        allowedTypes = ['none', 'fixed', 'multiply_input'];
      } else if (fieldType === 'repeater') {
        allowedTypes = ['none', 'repeater_total'];
      }

      return this.pricingTypes.filter((item) => allowedTypes.includes(item.value));
    },
    pricingHintByType(type) {
      const normalizedType = this.normalizePricingType(type);

      if (normalizedType === 'multiply_input') {
        return 'Example: 1,500 HUF × entered value 2.5 = 3,750 HUF.';
      }

      if (normalizedType === 'square_meter') {
        return 'Example: 1,500 HUF/m² × calculated product area.';
      }

      if (normalizedType === 'repeater_total') {
        return 'Example: (2 × 1.5 + 1 × 2) × 1,500 HUF.';
      }

      if (normalizedType === 'none') {
        return 'This field or value does not change the price.';
      }

      return 'The net amount is added once when the field/value is active.';
    },
    getPricingTypeText(type) {
      const normalizedType = this.normalizePricingType(type);
      const pricingType = this.pricingTypes.find((item) => item.value === normalizedType);

      return pricingType ? pricingType.text : normalizedType;
    },
    formatPricing(pricing) {
      const normalized = this.normalizePricing(pricing, 0, 'none');

      if (normalized.type === 'none') {
        return 'No price effect';
      }

      return this.getPricingTypeText(normalized.type) + ': ' + normalized.amount + ' HUF net';
    },
    applyPricingToLegacyValue(value) {
      const normalizedValue = value || initialValueForm();
      const pricing = this.normalizePricing(
        normalizedValue.pricing_json,
        normalizedValue.price_modifier || 0,
        normalizedValue.metadata_json && normalizedValue.metadata_json.price_modifier_type
      );

      normalizedValue.pricing_json = pricing;
      normalizedValue.price_modifier = pricing.amount;
      normalizedValue.metadata_json = Object.assign({}, normalizedValue.metadata_json || {}, {
        price_modifier_type: this.pricingToLegacyType(pricing.type),
      });

      return normalizedValue;
    },
    normalizeOption(option) {
      const source = option || {};
      const hasPricing = Object.prototype.hasOwnProperty.call(source, 'pricing_json')
        && source.pricing_json !== null;
      const normalized = Object.assign(initialOptionForm(), source);

      normalized.required = normalized.required === true || normalized.required === 1 || normalized.required === '1';
      normalized.is_extra = normalized.is_extra === true || normalized.is_extra === 1 || normalized.is_extra === '1';
      normalized.is_locked = normalized.is_locked === true || normalized.is_locked === 1 || normalized.is_locked === '1';
      normalized.has_tooltip = normalized.has_tooltip === true || normalized.has_tooltip === 1 || normalized.has_tooltip === '1';
      normalized.tooltip_text = normalized.tooltip_text || null;
      normalized.translations = this.normalizeTranslations(
        normalized.translations,
        { label: normalized.label, tooltip_text: normalized.tooltip_text },
        ['label', 'tooltip_text']
      );
      normalized.label = this.getLocalizedTranslationField(
        Object.assign({}, normalized, { translations: normalized.translations }),
        'label',
        'label'
      ) || normalized.key;
      normalized.label = normalized.translations.hu && normalized.translations.hu.label
        ? normalized.translations.hu.label
        : normalized.label;
      normalized.tooltip_text = normalized.translations.hu
        ? (normalized.translations.hu.tooltip_text || null)
        : normalized.tooltip_text;

      if (typeof normalized.metadata_json === 'string') {
        try {
          normalized.metadata_json = JSON.parse(normalized.metadata_json) || {};
        } catch (e) {
          normalized.metadata_json = {};
        }
      }

      if (!normalized.metadata_json || typeof normalized.metadata_json !== 'object' || Array.isArray(normalized.metadata_json)) {
        normalized.metadata_json = {};
      }

      normalized.values = Array.isArray(normalized.values)
        ? normalized.values.map((value) => this.normalizeValue(value))
        : [];

      let fallbackPricing = this.normalizePricing(
        null,
        normalized.price_modifier || 0,
        normalized.metadata_json.price_modifier_type || 'fixed'
      );

      if (!this.isChoiceOption(normalized) && normalized.values.length) {
        fallbackPricing = normalized.values[0].pricing_json;
      }

      normalized.pricing_json = hasPricing
        ? this.normalizePricing(source.pricing_json, 0, 'none')
        : fallbackPricing;

      if (this.isChoiceOption(normalized)) {
        normalized.pricing_json = initialPricing();
      }

      this.normalizeRepeaterMetadata(normalized);

      if (normalized.default_value === null || normalized.default_value === undefined) {
        normalized.default_value = null;
      }

      normalized.validation_json = normalized.validation_json || {};
      normalized.conditional_surcharge_json = this.normalizeConditionalSurcharge(normalized.conditional_surcharge_json);

      return normalized;
    },
    normalizeValue(value) {
      const source = value || {};
      const hasPricing = Object.prototype.hasOwnProperty.call(source, 'pricing_json')
        && source.pricing_json !== null;
      const normalized = Object.assign(initialValueForm(), source);
      normalized.translations = this.normalizeTranslations(
        normalized.translations,
        { label: normalized.label },
        ['label']
      );
      normalized.label = normalized.translations.hu && normalized.translations.hu.label
        ? normalized.translations.hu.label
        : (normalized.label || normalized.value);

      if (typeof normalized.metadata_json === 'string') {
        try {
          normalized.metadata_json = JSON.parse(normalized.metadata_json) || {};
        } catch (e) {
          normalized.metadata_json = {};
        }
      }

      if (!normalized.metadata_json || typeof normalized.metadata_json !== 'object' || Array.isArray(normalized.metadata_json)) {
        normalized.metadata_json = {};
      }

      normalized.pricing_json = this.normalizePricing(
        hasPricing ? source.pricing_json : null,
        normalized.price_modifier || 0,
        normalized.metadata_json.price_modifier_type || 'fixed'
      );

      return this.applyPricingToLegacyValue(normalized);
    },
    isChoiceOption(option) {
      return !!option && ['select', 'dropdown', 'radio', 'checkbox'].includes(option.type);
    },
    buildDefaultPriceValue(optionPayload, firstValue) {
      const existingValue = firstValue ? this.normalizeValue(firstValue) : initialValueForm();

      existingValue.value = optionPayload.key || existingValue.value || 'default';
      existingValue.translations = {};
      Object.keys(optionPayload.translations || {}).forEach((locale) => {
        existingValue.translations[locale] = {
          label: optionPayload.translations[locale].label || optionPayload.key || 'Default',
        };
      });
      existingValue.label = (existingValue.translations.hu && existingValue.translations.hu.label) || optionPayload.label || existingValue.label || optionPayload.key || 'Default';
      existingValue.pricing_json = this.normalizePricing(optionPayload.pricing_json, 0, 'none');
      existingValue.sort_order = 0;

      return this.applyPricingToLegacyValue(existingValue);
    },
    defaultRepeaterColumns() {
      return [
        {
          key: 'quantity',
          label: 'Db',
          translations: { hu: { label: 'Db' } },
          type: 'number',
          unit: 'db',
          required: true,
        },
        {
          key: 'length',
          label: 'Hossz',
          translations: { hu: { label: 'Hossz' } },
          type: 'number',
          unit: 'm',
          required: true,
        },
      ];
    },
    defaultRepeaterCalculator() {
      return {
        type: 'sum_product',
        quantity_field: 'quantity',
        value_field: 'length',
      };
    },
    getRepeaterValueMetadata(value) {
      const normalized = this.normalizeValue(value || {});
      return normalized.metadata_json || {};
    },
    getRepeaterCalculatorFromValues(values) {
      if (!Array.isArray(values)) {
        return null;
      }

      for (let index = 0; index < values.length; index += 1) {
        const metadata = this.getRepeaterValueMetadata(values[index]);

        if (metadata.repeater_calculator && typeof metadata.repeater_calculator === 'object' && !Array.isArray(metadata.repeater_calculator)) {
          return metadata.repeater_calculator;
        }

        if (metadata.calculator && typeof metadata.calculator === 'object' && !Array.isArray(metadata.calculator)) {
          return metadata.calculator;
        }
      }

      return null;
    },
    buildRepeaterColumnsFromValues(values) {
      if (!Array.isArray(values)) {
        return [];
      }

      return values
        .map((value) => {
          const normalizedValue = this.normalizeValue(value);
          const metadata = normalizedValue.metadata_json || {};
          const key = this.makeKey(normalizedValue.value || normalizedValue.label || '');
          const label = normalizedValue.label || normalizedValue.value || '';

          if (!key || !label) {
            return null;
          }

          return {
            key: key,
            label: label,
            translations: this.normalizeTranslations(
              normalizedValue.translations,
              { label: label },
              ['label']
            ),
            type: metadata.repeater_column_type || metadata.column_type || metadata.type || 'number',
            unit: metadata.repeater_unit || metadata.unit || '',
            required: metadata.repeater_required === true || metadata.repeater_required === 1 || metadata.repeater_required === '1' || metadata.required === true || metadata.required === 1 || metadata.required === '1',
          };
        })
        .filter((column) => column && column.key && column.label);
    },
    normalizeRepeaterMetadata(option) {
      if (!option || option.type !== 'repeater') {
        return;
      }

      if (!option.metadata_json || typeof option.metadata_json !== 'object' || Array.isArray(option.metadata_json)) {
        option.metadata_json = {};
      }

      const metadata = option.metadata_json;
      const metadataColumns = Array.isArray(metadata.repeater_columns)
        ? metadata.repeater_columns
        : (Array.isArray(metadata.columns) ? metadata.columns : []);
      const valueColumns = this.buildRepeaterColumnsFromValues(option.values || []);
      const existingColumns = metadataColumns.length ? metadataColumns : valueColumns;

      metadata.repeater_columns = existingColumns
        .map((column) => ({
          key: this.makeKey(column.key || column.value || column.label || ''),
          label: (column.translations && column.translations.hu && column.translations.hu.label)
            || column.label
            || column.key
            || column.value
            || '',
          translations: this.normalizeTranslations(
            column.translations,
            { label: column.label || column.key || column.value || '' },
            ['label']
          ),
          type: column.type === 'text' ? 'text' : 'number',
          unit: column.unit || column.repeater_unit || '',
          required: column.required === true || column.required === 1 || column.required === '1',
        }))
        .filter((column) => column.key && column.label);

      if (!metadata.repeater_columns.length) {
        metadata.repeater_columns = this.defaultRepeaterColumns();
      }

      const valueCalculator = this.getRepeaterCalculatorFromValues(option.values || []);
      const existingCalculator = metadata.repeater_calculator || metadata.calculator || valueCalculator || {};
      metadata.repeater_calculator = Object.assign(this.defaultRepeaterCalculator(), existingCalculator || {});

      if (!metadata.repeater_calculator.type) {
        metadata.repeater_calculator.type = 'sum_product';
      }

      if (!metadata.repeater_calculator.quantity_field && metadata.repeater_columns[0]) {
        metadata.repeater_calculator.quantity_field = metadata.repeater_columns[0].key;
      }

      if (!metadata.repeater_calculator.value_field && metadata.repeater_columns[1]) {
        metadata.repeater_calculator.value_field = metadata.repeater_columns[1].key;
      }
    },
    buildRepeaterColumnValues(optionPayload) {
      this.normalizeRepeaterMetadata(optionPayload);

      const columns = optionPayload.metadata_json.repeater_columns || this.defaultRepeaterColumns();
      const existingValues = Array.isArray(optionPayload.values) ? optionPayload.values.map((value) => this.normalizeValue(value)) : [];
      const existingByKey = {};

      existingValues.forEach((value) => {
        if (value && value.value) {
          existingByKey[value.value] = value;
        }
      });

      return columns.map((column, index) => {
        const existingValue = existingByKey[column.key] || existingValues[index] || initialValueForm();
        const valuePayload = this.normalizeValue(existingValue);
        const metadata = Object.assign({}, valuePayload.metadata_json || {});

        metadata.repeater_column_type = column.type === 'text' ? 'text' : 'number';
        metadata.repeater_unit = column.unit || '';
        metadata.repeater_required = column.required === true || column.required === 1 || column.required === '1';

        if (index === 0) {
          valuePayload.pricing_json = this.normalizePricing(optionPayload.pricing_json, 0, 'none');
          metadata.repeater_calculator = Object.assign(this.defaultRepeaterCalculator(), optionPayload.metadata_json.repeater_calculator || {});
        } else {
          valuePayload.pricing_json = initialPricing();
          delete metadata.repeater_calculator;
        }

        valuePayload.value = column.key;
        valuePayload.translations = this.normalizeTranslations(
          column.translations,
          { label: column.label || column.key },
          ['label']
        );
        valuePayload.label = valuePayload.translations.hu && valuePayload.translations.hu.label
          ? valuePayload.translations.hu.label
          : (column.label || column.key);
        valuePayload.sort_order = index;
        valuePayload.metadata_json = metadata;

        return this.applyPricingToLegacyValue(valuePayload);
      });
    },
    ensureRepeaterMetadata() {
      if (!this.optionForm.metadata_json || typeof this.optionForm.metadata_json !== 'object' || Array.isArray(this.optionForm.metadata_json)) {
        this.$set(this.optionForm, 'metadata_json', {});
      }

      this.normalizeRepeaterMetadata(this.optionForm);
      this.$forceUpdate();
    },
    handleAddRepeaterColumn() {
      this.ensureRepeaterMetadata();
      this.optionForm.metadata_json.repeater_columns.push({
        key: '',
        label: '',
        translations: { hu: { label: '' } },
        type: 'number',
        unit: '',
        required: false,
      });
    },
    handleRemoveRepeaterColumn(index) {
      this.ensureRepeaterMetadata();
      this.optionForm.metadata_json.repeater_columns.splice(index, 1);
    },
    handleAutofillRepeaterColumnKey(index) {
      this.ensureRepeaterMetadata();
      const column = this.optionForm.metadata_json.repeater_columns[index];

      const label = column ? this.getRepeaterColumnLabel(column) : '';

      if (column && !column.key && label) {
        column.key = this.makeKey(label);
      }
    },
    isMultiConditionOperator(operator) {
      return ['in', 'not_in'].includes(operator);
    },
    isComparisonConditionOperator(operator) {
      return ['>', '>=', '<', '<='].includes(operator);
    },
    parseConditionValueForPayload(value, operator) {
      if (this.isMultiConditionOperator(operator)) {
        if (Array.isArray(value)) {
          return value
            .map((item) => String(item).trim())
            .filter((item) => item !== '');
        }

        return String(value || '')
          .split(/[\n,;]+/)
          .map((item) => item.trim())
          .filter((item) => item !== '');
      }

      if (Array.isArray(value)) {
        value = value.length ? value[0] : null;
      }

      if (value === null || value === undefined) {
        return null;
      }

      const normalizedValue = String(value).trim();

      if (!normalizedValue) {
        return null;
      }

      if (this.isComparisonConditionOperator(operator) && /^-?\d+([.,]\d+)?$/.test(normalizedValue)) {
        return Number(normalizedValue.replace(',', '.'));
      }

      return normalizedValue;
    },
    parseFormNumber(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const normalizedValue = String(value).replace(/\s+/g, '').replace(',', '.');
      const numberValue = Number(normalizedValue);

      return isNaN(numberValue) ? null : numberValue;
    },
    getAllOptionFieldItems() {
      const options = this.clone(this.localOptions || []);

      if (this.optionForm && this.optionForm.key) {
        const existingIndex = options.findIndex((option) => option.key === this.optionForm.key);

        if (existingIndex === -1) {
          options.push(this.clone(this.optionForm));
        } else {
          options.splice(existingIndex, 1, this.clone(this.optionForm));
        }
      }

      return options
        .filter((option) => option && option.key)
        .map((option) => ({
          text: (this.getLocalizedOptionLabel(option) || option.key) + ' (' + option.key + ')',
          value: option.key,
          type: option.type,
        }));
    },
    getConditionalSurchargeFieldItems(basis) {
      const allowedTypes = basis === 'repeater_total'
        ? ['repeater']
        : ['number'];

      return this.getAllOptionFieldItems()
        .filter((item) => allowedTypes.includes(item.type));
    },
    normalizeConditionalSurchargeBasis(basis) {
      const normalized = String(basis || 'square_meter').trim().toLowerCase();
      const aliases = {
        sqm: 'square_meter',
        m2: 'square_meter',
        pieces: 'quantity',
        count: 'quantity',
        input: 'option_value',
        input_value: 'option_value',
        field_value: 'option_value',
        per_repeater_total: 'repeater_total',
      };
      const resolved = aliases[normalized] || normalized;

      return ['square_meter', 'quantity', 'option_value', 'repeater_total'].includes(resolved)
        ? resolved
        : 'square_meter';
    },
    normalizeConditionalSurchargeAmountType(amountType) {
      const normalized = String(amountType || 'fixed').trim().toLowerCase();

      if (['per_unit', 'multiply_basis'].includes(normalized)) {
        return 'per_basis_unit';
      }

      return ['fixed', 'per_basis_unit'].includes(normalized)
        ? normalized
        : 'fixed';
    },
    normalizeConditionalSurchargeScope(scope) {
      const normalized = String(scope || 'product').trim().toLowerCase();
      const aliases = {
        once: 'product',
        item: 'product',
        line: 'product',
        cart: 'order',
        basket: 'order',
      };
      const resolved = aliases[normalized] || normalized;

      return ['product', 'order', 'group'].includes(resolved)
        ? resolved
        : 'product';
    },
    getConditionalSurchargeBasisUnit(basis, basisOptionKey) {
      const normalizedBasis = this.normalizeConditionalSurchargeBasis(basis);

      if (normalizedBasis === 'square_meter') {
        return 'm²';
      }

      if (normalizedBasis === 'quantity') {
        return 'db';
      }

      const fieldKey = basisOptionKey || this.conditionalSurchargeBasisOptionKey;
      const field = (this.localOptions || []).find((option) => option.key === fieldKey)
        || (this.optionForm.key === fieldKey ? this.optionForm : null);

      return field && field.unit ? field.unit : 'egység';
    },
    getConditionalSurchargeBasisText(basis) {
      const normalizedBasis = this.normalizeConditionalSurchargeBasis(basis);
      const selected = this.conditionalSurchargeBasisOptions.find((item) => item.value === normalizedBasis);

      return selected ? selected.text : normalizedBasis;
    },
    getConditionalSurchargeScopeText(scope) {
      const normalizedScope = this.normalizeConditionalSurchargeScope(scope);
      const selected = this.conditionalSurchargeScopeOptions.find((item) => item.value === normalizedScope);

      return selected ? selected.text : normalizedScope;
    },
    normalizeConditionalSurcharge(value) {
      if (typeof value === 'string') {
        try {
          value = JSON.parse(value) || null;
        } catch (e) {
          value = null;
        }
      }

      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
      }

      const enabled = value.enabled === true || value.enabled === 1 || value.enabled === '1' || value.enabled === 'true';
      const threshold = this.parseFormNumber(value.threshold);
      const amountNet = this.parseFormNumber(value.amount_net);

      if (!enabled || threshold === null || threshold <= 0 || amountNet === null || amountNet <= 0) {
        return null;
      }

      const basis = this.normalizeConditionalSurchargeBasis(value.basis);
      const scope = this.normalizeConditionalSurchargeScope(value.scope || value.apply_type);
      const fallbackOptionKey = this.optionForm && this.optionForm.key
        ? this.optionForm.key
        : null;
      const needsBasisOption = ['square_meter', 'option_value', 'repeater_total'].includes(basis);

      return {
        enabled: true,
        basis: basis,
        basis_option_key: needsBasisOption
          ? (value.basis_option_key || value.source_option_key || fallbackOptionKey)
          : null,
        operator: value.operator || 'below',
        threshold: threshold,
        amount_type: this.normalizeConditionalSurchargeAmountType(value.amount_type),
        amount_net: amountNet,
        scope: scope,
        aggregation_key: scope === 'product'
          ? null
          : (value.aggregation_key || value.surcharge_key || (fallbackOptionKey ? fallbackOptionKey + '_conditional_surcharge' : null)),
        group_option_key: scope === 'group'
          ? (value.group_option_key || value.group_by_option_key || fallbackOptionKey)
          : null,
        label: value.label || '',
      };
    },
    buildConditionalSurchargePayload() {
      if (!this.hasConditionalSurcharge) {
        return null;
      }

      const threshold = this.parseFormNumber(this.conditionalSurchargeThreshold);
      const amountNet = this.parseFormNumber(this.conditionalSurchargeAmountNet);
      const basis = this.normalizeConditionalSurchargeBasis(this.conditionalSurchargeBasis);
      const scope = this.normalizeConditionalSurchargeScope(this.conditionalSurchargeScope);
      const basisNeedsOption = ['square_meter', 'option_value', 'repeater_total'].includes(basis);
      const basisOptionKey = basisNeedsOption
        ? String(this.conditionalSurchargeBasisOptionKey || '').trim()
        : null;
      const aggregationKey = scope === 'product'
        ? null
        : String(this.conditionalSurchargeAggregationKey || '').trim();
      const groupOptionKey = scope === 'group'
        ? String(this.conditionalSurchargeGroupOptionKey || '').trim()
        : null;

      if (threshold === null || threshold <= 0) {
        this.showError('Conditional surcharge limit must be greater than 0.');
        return false;
      }

      if (amountNet === null || amountNet <= 0) {
        this.showError('Conditional surcharge net amount must be greater than 0.');
        return false;
      }

      if (basisNeedsOption && !basisOptionKey) {
        this.showError('Select the source field used to calculate the conditional surcharge.');
        return false;
      }

      if (scope !== 'product' && !aggregationKey) {
        this.showError('Surcharge key is required for order and group scopes.');
        return false;
      }

      if (scope === 'group' && !groupOptionKey) {
        this.showError('Select the field used to group matching products.');
        return false;
      }

      const unit = this.getConditionalSurchargeBasisUnit(basis);
      const defaultLabel = threshold + ' ' + unit + ' alatti felár';

      return {
        enabled: true,
        basis: basis,
        basis_option_key: basisOptionKey,
        operator: this.conditionalSurchargeOperator || 'below',
        threshold: threshold,
        amount_type: this.normalizeConditionalSurchargeAmountType(this.conditionalSurchargeAmountType),
        amount_net: amountNet,
        scope: scope,
        aggregation_key: aggregationKey,
        group_option_key: groupOptionKey,
        label: String(this.conditionalSurchargeLabel || '').trim() || defaultLabel,
      };
    },
    getConditionalSurchargeOperatorText(operator) {
      const selectedOperator = this.conditionalSurchargeOperators.find((item) => item.value === operator);

      return selectedOperator ? selectedOperator.text : (operator || 'below');
    },
    formatConditionalSurcharge(rule) {
      rule = this.normalizeConditionalSurcharge(rule);

      if (!rule) {
        return '-';
      }

      const amountSuffix = rule.amount_type === 'per_basis_unit'
        ? ' HUF/' + this.getConditionalSurchargeBasisUnit(rule.basis, rule.basis_option_key)
        : ' HUF';
      const parts = [
        this.getConditionalSurchargeBasisText(rule.basis),
        this.getConditionalSurchargeOperatorText(rule.operator),
        rule.threshold + ' ' + this.getConditionalSurchargeBasisUnit(rule.basis, rule.basis_option_key),
        '+ ' + rule.amount_net + amountSuffix + ' net',
        this.getConditionalSurchargeScopeText(rule.scope),
      ];

      if (rule.scope !== 'product' && rule.aggregation_key) {
        parts.push('key: ' + rule.aggregation_key);
      }

      if (rule.scope === 'group' && rule.group_option_key) {
        parts.push('group: ' + rule.group_option_key);
      }

      return parts.join(' · ');
    },
    fillConditionalSurchargeForm(rule) {
      rule = this.normalizeConditionalSurcharge(rule);

      this.hasConditionalSurcharge = !!rule;
      this.conditionalSurchargeBasis = rule ? rule.basis : 'square_meter';
      this.conditionalSurchargeBasisOptionKey = rule ? rule.basis_option_key : null;
      this.conditionalSurchargeOperator = rule ? rule.operator : 'below';
      this.conditionalSurchargeThreshold = rule ? rule.threshold : null;
      this.conditionalSurchargeAmountType = rule ? rule.amount_type : 'fixed';
      this.conditionalSurchargeAmountNet = rule ? rule.amount_net : null;
      this.conditionalSurchargeScope = rule ? rule.scope : 'product';
      this.conditionalSurchargeAggregationKey = rule ? (rule.aggregation_key || '') : '';
      this.conditionalSurchargeGroupOptionKey = rule ? rule.group_option_key : null;
      this.conditionalSurchargeLabel = rule ? (rule.label || '') : '';
    },
    conditionPayloadHasValue(value) {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== null && value !== undefined && value !== '';
    },
    formatConditionValueForForm(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }

      if (value === null || value === undefined) {
        return null;
      }

      return String(value);
    },
    formatConditionValueForDisplay(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }

      if (value === null || value === undefined || value === '') {
        return '-';
      }

      return value;
    },
    getConditionOperatorText(operator) {
      const conditionOperator = this.conditionOperators.find((item) => item.value === operator);

      return conditionOperator ? conditionOperator.value : (operator || '=');
    },
    formatCondition(condition) {
      if (!condition || !condition.field) {
        return '-';
      }

      return [
        condition.field,
        this.getConditionOperatorText(condition.operator || '='),
        this.formatConditionValueForDisplay(condition.value),
      ].join(' ');
    },
    emitChange() {
      this.$emit('input', this.clone(this.localOptions));
    },
    makeKey(value) {
      return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },
    handleAutofillOptionKey() {
      const label = this.getOptionTranslationField('label') || this.getLocalizedOptionLabel(this.optionForm);

      if (!this.optionForm.key && label) {
        this.optionForm.key = this.makeKey(label);
      }
    },
    handleAutofillValue() {
      const label = this.getValueTranslationField('label') || this.getLocalizedValueLabel(this.valueForm);

      if (!this.valueForm.value && label) {
        this.valueForm.value = this.makeKey(label);
      }
    },
    buildOptionPayload() {
      const payload = this.clone(this.optionForm);
      payload.translations = this.normalizeTranslations(
        payload.translations,
        { label: payload.label, tooltip_text: payload.tooltip_text },
        ['label', 'tooltip_text']
      );
      payload.label = payload.translations.hu && payload.translations.hu.label
        ? payload.translations.hu.label
        : (payload.label || payload.key);
      payload.tooltip_text = payload.translations.hu
        ? (payload.translations.hu.tooltip_text || null)
        : (payload.tooltip_text || null);
      const isChoice = ['select', 'dropdown', 'radio', 'checkbox'].includes(payload.type);

      if (payload.default_value === undefined || payload.default_value === '') {
        payload.default_value = null;
      } else if (payload.default_value !== null) {
        payload.default_value = String(payload.default_value);
      }

      if (!payload.has_tooltip) {
        payload.tooltip_text = null;
        Object.keys(payload.translations || {}).forEach((locale) => {
          payload.translations[locale].tooltip_text = null;
        });
      } else {
        Object.keys(payload.translations || {}).forEach((locale) => {
          const tooltipText = payload.translations[locale].tooltip_text;
          payload.translations[locale].tooltip_text = tooltipText === null || tooltipText === undefined
            ? null
            : (String(tooltipText).trim() || null);
        });
        payload.tooltip_text = payload.translations.hu
          ? (payload.translations.hu.tooltip_text || null)
          : payload.tooltip_text;
      }

      if (!payload.metadata_json || typeof payload.metadata_json !== 'object' || Array.isArray(payload.metadata_json)) {
        payload.metadata_json = {};
      }

      payload.pricing_json = this.normalizePricing(
        payload.pricing_json,
        payload.price_modifier || 0,
        payload.metadata_json.price_modifier_type || 'fixed'
      );

      if (payload.type !== 'number') {
        payload.validation_json = {};
      }

      if (payload.type === 'repeater') {
        this.normalizeRepeaterMetadata(payload);
      }

      payload.conditions_json = null;
      const conditionOperator = this.conditionOperator || '=';
      const conditionValue = this.parseConditionValueForPayload(this.conditionValue, conditionOperator);

      if (this.hasCondition && this.conditionField && this.conditionPayloadHasValue(conditionValue)) {
        payload.conditions_json = {
          show_if: {
            field: this.conditionField,
            operator: conditionOperator,
            value: conditionValue,
          },
        };
      }

      const conditionalSurchargePayload = this.buildConditionalSurchargePayload();

      if (conditionalSurchargePayload === false) {
        return false;
      }

      payload.conditional_surcharge_json = conditionalSurchargePayload;

      if (isChoice) {
        // Choice fields are priced per selectable value, not on the field itself.
        payload.pricing_json = initialPricing();
        payload.values = Array.isArray(payload.values)
          ? payload.values.map((value) => {
            const normalizedValue = this.normalizeValue(value);
            normalizedValue.label = normalizedValue.translations.hu && normalizedValue.translations.hu.label
              ? normalizedValue.translations.hu.label
              : (normalizedValue.label || normalizedValue.value);
            return this.applyPricingToLegacyValue(normalizedValue);
          })
          : [];
      } else if (payload.type === 'repeater') {
        // Repeater column definitions are stored as item_option_values records.
        // The first value row also carries the price modifier and calculator config.
        payload.values = this.buildRepeaterColumnValues(payload);
      } else {
        const firstValue = Array.isArray(payload.values) && payload.values.length ? payload.values[0] : null;
        payload.values = [this.buildDefaultPriceValue(payload, firstValue)];
      }

      // Legacy columns remain on item_option_values. pricing_json is now the canonical source.
      delete payload.price_modifier;

      // ItemOption does not have metadata_json in this project.
      // Repeater config and price metadata are persisted on item_option_values.metadata_json.
      delete payload.metadata_json;

      return payload;
    },
    handleSaveOption() {
      this.$refs.webOptionForm.validate();
      if (!this.formValid) return;

      const activeLocaleLabel = this.getExactTranslationField(
        this.optionForm,
        'label',
        this.activeLocale
      );

      if (!activeLocaleLabel || !String(activeLocaleLabel).trim()) {
        this.showError('The field label is required for ' + this.activeLocale.toUpperCase() + '.');
        return;
      }

      if (this.isChoiceField) {
        const missingLocalizedValue = (this.optionForm.values || []).find((value) => {
          const label = this.getExactTranslationField(value, 'label', this.activeLocale);
          return !label || !String(label).trim();
        });

        if (missingLocalizedValue) {
          this.showError(
            'Every selectable value must have a label for ' + this.activeLocale.toUpperCase() + '.'
          );
          return;
        }
      }

      if (this.optionForm.type === 'repeater') {
        this.ensureRepeaterMetadata();
        const missingLocalizedColumn = (this.optionForm.metadata_json.repeater_columns || []).find((column) => {
          const label = this.getExactTranslationField(column, 'label', this.activeLocale);
          return !label || !String(label).trim();
        });

        if (missingLocalizedColumn) {
          this.showError(
            'Every repeater column must have a label for ' + this.activeLocale.toUpperCase() + '.'
          );
          return;
        }
      }

      const sameKey = this.localOptions.find((option, index) => {
        return option.key === this.optionForm.key && index !== this.editedOptionIndex;
      });

      if (sameKey) {
        this.showError('This field key already exists on this item.');
        return;
      }

      const payload = this.buildOptionPayload();

      if (payload === false) {
        return;
      }

      if (this.editedOptionIndex === -1) {
        this.localOptions.push(payload);
      } else {
        this.localOptions.splice(this.editedOptionIndex, 1, payload);
      }

      this.emitChange();
      this.resetOptionForm();
    },
    handleEditOption(item) {
      const index = this.localOptions.findIndex((option) => option.key === item.key);
      this.editedOptionIndex = index;
      this.optionForm = this.normalizeOption(this.clone(item));
      this.resetValueForm();

      const condition = this.optionForm.conditions_json && this.optionForm.conditions_json.show_if;
      this.hasCondition = !!condition;
      this.conditionField = condition ? condition.field : null;
      this.conditionOperator = condition ? (condition.operator || '=') : '=';
      this.conditionValue = condition ? this.formatConditionValueForForm(condition.value) : null;
      this.fillConditionalSurchargeForm(this.optionForm.conditional_surcharge_json);
    },
    async handleDeleteOption(item) {
      const confirmed = await this.confirmDelete(
        'Delete field?',
        'Are you sure you want to delete this field?'
      );

      if (!confirmed) return;

      this.localOptions = this.localOptions.filter((option) => option.key !== item.key);
      this.emitChange();

      if (this.optionForm.key === item.key) {
        this.resetOptionForm();
      }
    },
    cancelEditOption() {
      this.resetOptionForm();
    },
    resetOptionForm() {
      this.optionForm = initialOptionForm();
      this.valueForm = initialValueForm();
      this.editedOptionIndex = -1;
      this.editedValueIndex = -1;
      this.hasCondition = false;
      this.conditionField = null;
      this.conditionOperator = '=';
      this.conditionValue = null;
      this.fillConditionalSurchargeForm(null);

      this.$nextTick(() => {
        if (this.$refs.webOptionForm) {
          this.$refs.webOptionForm.resetValidation();
        }
      });
    },
    handleAddValue() {
      const activeLocaleLabel = this.getExactTranslationField(
        this.valueForm,
        'label',
        this.activeLocale
      );

      if (!activeLocaleLabel || !String(activeLocaleLabel).trim() || !this.valueForm.value) {
        this.showError(
          'Value label for ' + this.activeLocale.toUpperCase() + ' and value key are required.'
        );
        return;
      }

      const sameValue = (this.optionForm.values || []).find((value, index) => {
        return value.value === this.valueForm.value && index !== this.editedValueIndex;
      });

      if (sameValue) {
        this.showError('This value key already exists on this field.');
        return;
      }

      if (!Array.isArray(this.optionForm.values)) {
        this.optionForm.values = [];
      }

      const valuePayload = this.normalizeValue(this.clone(this.valueForm));

      if (this.editedValueIndex === -1) {
        this.optionForm.values.push(valuePayload);
      } else {
        this.optionForm.values.splice(this.editedValueIndex, 1, valuePayload);
      }

      this.resetValueForm();
    },
    handleEditValue(item) {
      const index = this.optionForm.values.findIndex((value) => value.value === item.value);
      this.editedValueIndex = index;
      this.valueForm = this.normalizeValue(this.clone(item));
    },
    async handleDeleteValue(item) {
      const confirmed = await this.confirmDelete(
        'Delete value?',
        'Are you sure you want to delete this selectable value?'
      );

      if (!confirmed) return;

      this.optionForm.values = this.optionForm.values.filter((value) => value.value !== item.value);
      this.resetValueForm();
    },
    resetValueForm() {
      this.valueForm = initialValueForm();
      this.editedValueIndex = -1;
    },
  },
};
</script>

<style scoped>
.item-web-options {
  color: #263238;
}

.web-options-card {
  border: 1px solid #e6ebf1 !important;
  border-radius: 14px !important;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
}

.web-options-header {
  padding: 22px 24px 18px !important;
  border-bottom: 1px solid #edf1f5;
  background: linear-gradient(135deg, #fbfdff 0%, #f5f8fb 100%);
}

.web-options-title {
  color: #1f2d3d;
  font-weight: 600;
  line-height: 1.2;
}

.web-options-action {
  border-radius: 9px;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.22);
}

.web-options-body {
  padding: 22px 24px 24px !important;
}

.web-options-alert {
  margin-bottom: 18px;
  border-radius: 10px;
}

.locale-context-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border: 1px solid #cfe0f5;
  border-left: 5px solid #1976d2;
  border-radius: 10px;
  background: #f5f9ff;
}

.locale-context-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.locale-context-chip {
  flex: 0 0 auto;
  font-weight: 800;
  letter-spacing: 0.06em;
}

.locale-context-title {
  color: #19324d;
  font-size: 14px;
  font-weight: 700;
}

.locale-context-description {
  margin-top: 2px;
  color: #60758a;
  font-size: 12px;
  line-height: 1.4;
}

.locale-context-legend,
.translation-status-list {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.locale-context-legend {
  justify-content: flex-end;
  flex: 0 0 auto;
}

.locale-legend-chip,
.translation-status-chip {
  margin: 0 !important;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.web-options-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.web-options-panel {
  padding: 18px 18px 8px;
  border: 1px solid #eef2f6;
  border-radius: 12px;
  background: #ffffff;
}

.web-options-panel--muted {
  background: #fafcff;
}

.pricing-section {
  margin-top: 10px;
  padding-top: 16px;
  border-top: 1px solid #eef2f6;
}

.pricing-help-alert {
  margin-bottom: 6px;
}

.section-title {
  margin: 0 0 12px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.4;
  text-transform: uppercase;
}

.form-row {
  margin-top: 0;
}

.compact-row {
  margin-top: -8px;
}

.checkbox-col,
.value-actions-col {
  display: flex;
  align-items: center;
}

.value-actions-col {
  flex-wrap: wrap;
  padding-top: 20px;
}

.value-action-button,
.form-footer-button {
  border-radius: 9px;
}

.form-footer-row {
  margin-top: -8px;
}

.form-footer {
  display: flex;
  align-items: center;
  padding-top: 4px;
  padding-bottom: 2px;
}

.options-list-section {
  margin-top: 24px;
  padding-top: 22px;
  border-top: 1px solid #edf1f5;
}

.table-card {
  overflow: hidden;
  border: 1px solid #e7ecf2;
  border-radius: 12px;
  background: #ffffff;
}

.table-card--compact {
  margin-bottom: 10px;
}

.web-options-table {
  background: transparent;
}

.table-action-icon {
  padding: 6px;
  border-radius: 8px;
  color: #607d8b;
  transition: background-color 0.16s ease, color 0.16s ease, transform 0.16s ease;
}

.table-action-icon:hover {
  color: #1976d2;
  background: #eef6ff;
  transform: translateY(-1px);
}

.table-action-icon--danger:hover {
  color: #d32f2f;
  background: #fff1f1;
}

.required-chip {
  font-weight: 600;
}

.table-muted-text {
  color: #52616f;
}

.table-localized-label {
  color: #263238;
  font-weight: 600;
}

.missing-translation-label {
  display: inline-flex;
  align-items: center;
  padding: 3px 7px;
  border: 1px dashed #ef9a9a;
  border-radius: 6px;
  color: #c62828;
  background: #fff7f7;
  font-size: 11px;
  font-weight: 700;
}

.table-empty-text {
  color: #a0a8b2;
}

@media (max-width: 600px) {
  .web-options-header,
  .web-options-body {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .web-options-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .web-options-action {
    margin-top: 14px;
  }

  .web-options-panel {
    padding: 16px 12px 6px;
  }

  .locale-context-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .locale-context-legend {
    justify-content: flex-start;
  }

  .form-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .form-footer-button {
    margin: 0 0 8px !important;
  }
}
</style>
